import React from 'react';
import { Box, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// SmartSend part B — "שורה לדוגמה k מתוך n" with the two arrows. Five deterministic candidates
// (offset 0..4, contract §5), so two operators looking at the same campaign see the same rows.
//
// RTL: the two arrows are swapped, not mirrored by a stylesheet. `makeStyles` inside the
// component tree DOES get the RTL theme, but the ARROW GLYPH is content, not layout — jss-rtl
// flips margins, never which icon is drawn — so "previous" must point the correct way
// explicitly. This is the same `isRTL ? ... : ...` branch `whatsappStyles.js` uses throughout.

const useStyles = makeStyles((theme) => ({
    bar: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' },
    unmapped: { color: 'var(--pls-d-danger, #c0392b)', fontWeight: 600 },
}));

interface Props {
    offset: number;                 // 0-based — WHAT THE OPERATOR ASKED FOR
    // 🔴 WHAT IS ACTUALLY ON SCREEN, when the caller can tell the two apart. Defaults to
    // `offset`, so every caller that cannot — the test dialog, which gates the whole row on the
    // two agreeing — is unchanged.
    //
    // The problem it solves is not cosmetic and it is not about the bubble: the label and the
    // content move at DIFFERENT MOMENTS. The label follows `preview.offset`, which changes on
    // the CLICK; the bubble follows `preview.row`, which changes when the answer LANDS. While
    // that round trip is open, either the label lies or the bubble blinks — there is no third
    // option, and the owner settled it on 2026-09-14: the label describes what is shown.
    //
    // The deciding reason is accessibility, not taste. This label carries `aria-live="polite"`
    // (below), so a screen reader ANNOUNCES it. "שורה 4 מתוך 5" spoken while row 3 is on screen
    // is worse for a blind operator than for a sighted one: they hear a number and have no way
    // to discover that the content beneath it belongs to a different row.
    //
    // ⚠️ `go()` and `disabled` keep reading `offset`, deliberately — they are about where the
    // operator may navigate TO, and clamping them to the landed row would let a fast clicker
    // walk past the end of the candidate set.
    labelOffset?: number;
    count: number;                  // candidates available, capped at 5 by the caller
    unboundCount: number;           // variables with no column and no constant
    disabled?: boolean;
    onChange: (offset: number) => void;
}

const WaSampleRowStepper: React.FC<Props> = ({
    offset, labelOffset, count, unboundCount, disabled = false, onChange,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);

    if (count <= 0) return null;

    const go = (delta: number) => {
        const next = offset + delta;
        if (next < 0 || next >= count) return;
        onChange(next);
    };

    const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
    const NextIcon = isRTL ? ChevronLeft : ChevronRight;

    return (
        <Box className={classes.bar}>
            <IconButton
                size="small"
                disabled={disabled || offset <= 0}
                onClick={() => go(-1)}
                aria-label={t('DataSources.send.whatsapp.mapping.stepper.prev')}
            >
                <PrevIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" aria-live="polite">
                {/* 🔴 `?? offset`, and it must be `??` — see the caller. A prop default only
                    applies to `undefined`; `null` passes straight through, and `null + 1 === 1`
                    would announce "שורה 1 מתוך N" over an empty preview. */}
                {t('DataSources.send.whatsapp.mapping.stepper.of', {
                    k: ((labelOffset ?? offset) + 1).toLocaleString(),
                    n: count.toLocaleString(),
                })}
            </Typography>
            <IconButton
                size="small"
                disabled={disabled || offset >= count - 1}
                onClick={() => go(1)}
                aria-label={t('DataSources.send.whatsapp.mapping.stepper.next')}
            >
                <NextIcon fontSize="small" />
            </IconButton>
            {/* 🔴 The "not mapped" signal lives HERE and on the variable row — never inside the
                phone. The bubble shows what the recipient would receive; a red mark inside it
                would read as part of the message. */}
            {unboundCount > 0 && (
                <Typography variant="body2" component="span" className={classes.unmapped}>
                    {/* The EXISTING e-mail key, reused verbatim — "{{n}} משתנים לא ממופים" says
                        exactly this and already exists in all three languages. A new key under
                        `send.whatsapp.*` would fail the i18n self-test: it is not in the
                        contract's §7 list, and the test rejects a key that is in use and
                        unlisted just as firmly as one that is listed and untranslated. */}
                    {t('DataSources.send.mapping.unmappedCount', { n: unboundCount.toLocaleString() })}
                </Typography>
            )}
        </Box>
    );
};

export default WaSampleRowStepper;
